import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Heading from '../ui/PageUI/Heading';
import Subheading from '../ui/PageUI/SubHeading';
import Paragraph from '../ui/PageUI/Paragraph';
import ImageWithText from '../ui/PageUI/ImageWithText';
import imageSrc from '../Assets/Legal.png';
import Button from '../components/Button';
import Section from '../components/Section';
import { 
  FiFileText, 
  FiShield, 
  FiLock, 
  FiAlertTriangle, 
  FiXOctagon,
} from 'react-icons/fi';

const Legal = () => {
  const legalDocuments = [
    {
      title: "Terms of Use",
      description: "Understand the terms and conditions for using our platform.",
      icon: <FiFileText className="w-8 h-8" />,
      link: "/legal/terms-of-use",
      color: "text-blue-500"
    },
    {
      title: "Arbitration Agreement",
      description: "Review the process for resolving disputes through arbitration.", 
      link: "/legal/arbitration-agreement",
      color: "text-purple-500"
    },
    {
      title: "Privacy Policy",
      description: "Learn how we protect your personal information and data.",
      icon: <FiShield className="w-8 h-8" />,
      link: "/legal/privacy",
      color: "text-teal-500"
    },
    {
      title: "Security Policy",
      description: "Discover the measures we take to ensure the security of your assets.",
      icon: <FiLock className="w-8 h-8" />,
      link: "/legal/security",
      color: "text-green-500"
    },
    {
      title: "Risk Disclosure",
      description: "Understand the risks associated with investing in cryptocurrencies.",
      icon: <FiAlertTriangle className="w-8 h-8" />,
      link: "/legal/general-risk-warning",
      color: "text-yellow-500"
    },
    {
      title: "Prohibited Use",
      description: "Activities and behaviors not permitted on our platform.",
      icon: <FiXOctagon className="w-8 h-8" />,
      link: "/legal/prohibited-use-policy",
      color: "text-red-500"
    }
  ];

  const complianceFeatures = [
    { 
      text: "Know-Your-Customer (KYC) verification", 
      link: "/KYC",
      icon: "🆔"
    },
    { 
      text: "Compliance Requirements", 
      link: "/Compliance-Requirements",
      icon: "📜"
    },
    { 
      text: "Digital Asset Disclosures", 
      link: "/Digital-Assets-Disclosure",
      icon: "💱"
    },
    {
      text: "AML Policy",
      link: "/aml-policy",
      icon: "🕵️"
    }
  ];

  return (
    <Section id="legal" >
      {/* Hero Section */}
      <div className="relative max-w-7xl mx-auto px-6 py-28 text-center">
        <div className="absolute inset-0 overflow-hidden opacity-10">
          <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-blue-500 rounded-full filter blur-3xl opacity-20"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-purple-500 rounded-full filter blur-3xl opacity-20"></div>
        </div>
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="inline-block px-4 py-1 bg-blue-900/30 rounded-full text-blue-400 text-sm font-medium mb-6 border border-blue-800/50">
            Legal Framework
          </div>
          <Heading 
            text="Transparency & Compliance" 
            className="text-4xl sm:text-5xl font-bold mb-4 text-white"
          />
          <Subheading
            text="Building trust through clear policies and robust security"
            className="text-xl text-gray-400 max-w-3xl mx-auto"
          />
          <p className="text-gray-400 mt-6 max-w-2xl mx-auto">
            At Laani, we believe legal clarity is foundational to crypto adoption. Our comprehensive policies are designed to protect you while maintaining accessibility.
          </p>
        </motion.div>
      </div>

      {/* Documents Grid */}
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="text-center mb-16">
          <div className="inline-block px-4 py-1 bg-purple-900/30 rounded-full text-purple-400 text-sm font-medium mb-6 border border-purple-800/50">
            Legal Documentation
          </div>
          <Heading 
            text="Our Policies & Disclosures" 
            className="text-3xl font-bold mb-4 text-white"
          />
          <p className="text-gray-400 max-w-2xl mx-auto">
            Easily access all legal documents governing our platform and services
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {legalDocuments.map((doc, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: index * 0.1 }}
              viewport={{ once: true, margin: "-100px" }}
              whileHover={{ y: -5 }}
            >
              <Link
                to={doc.link}
                className={`group block h-full p-8 rounded-xl border border-gray-700/50 bg-gray-800/50 hover:border-${doc.color.split('-')[1]}-400/30 transition-all duration-300 hover:shadow-lg`}
              >
                <div className={`${doc.color} mb-6 group-hover:text-${doc.color.split('-')[1]}-400 transition-colors`}>
                  {doc.icon}
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">{doc.title}</h3>
                <p className="text-gray-400 mb-6">{doc.description}</p>
                <div className={`text-sm font-medium ${doc.color} group-hover:text-${doc.color.split('-')[1]}-400 transition-colors flex items-center`}>
                  View document <span className="ml-2">→</span>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Compliance Section */}
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="bg-gray-800/30 rounded-2xl border border-gray-700/50 overflow-hidden">
          <ImageWithText
            heading="Regulatory Excellence"
            subheading="Compliance First Approach"
            paragraph="Laani operates with a proactive compliance framework that exceeds industry standards. Our multi-layered approach ensures we meet all regulatory requirements while maintaining an exceptional user experience."
            bullets={complianceFeatures.map(feature => ({
              text: (
                <Link 
                  to={feature.link} 
                  className="flex items-center gap-3 hover:text-blue-400 transition-colors"
                >
                  <span className="text-lg">{feature.icon}</span>
                  {feature.text}
                </Link>
              )
            }))}
            imageUrl={imageSrc}
            reverseLayout={true}
            ctaText="Explore Our Compliance"
            ctaLink="/compliance"
            className="p-0"
          />
        </div>
      </div>

      {/* Commitment Section */}
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="bg-gradient-to-r from-blue-900/40 to-purple-900/40 rounded-2xl p-12 border border-gray-700/50 text-center">
          <div className="inline-block px-4 py-1 bg-blue-900/30 rounded-full text-blue-400 text-sm font-medium mb-6 border border-blue-800/50">
            Our Promise
          </div>
          <Heading 
            text="Security as a Foundation" 
            className="text-3xl font-bold mb-6 text-white"
          />
          <Paragraph
            text="We believe that true financial innovation can only happen when users feel completely secure. That's why we've built our platform with institutional-grade security measures, regular third-party audits, and transparent communication about all policies and procedures."
            className="text-gray-400 max-w-3xl mx-auto text-lg"
          />
          <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="bg-gray-800/50 p-4 rounded-lg border border-gray-700/50">
              <div className="text-blue-400 text-2xl mb-2">🔐</div>
              <div className="text-white font-medium">Bank-Level Encryption</div>
            </div>
            <div className="bg-gray-800/50 p-4 rounded-lg border border-gray-700/50">
              <div className="text-blue-400 text-2xl mb-2">📋</div>
              <div className="text-white font-medium">Quarterly Audits</div>
            </div>
            <div className="bg-gray-800/50 p-4 rounded-lg border border-gray-700/50">
              <div className="text-blue-400 text-2xl mb-2">👁️</div>
              <div className="text-white font-medium">Real-Time Monitoring</div>
            </div>
            <div className="bg-gray-800/50 p-4 rounded-lg border border-gray-700/50">
              <div className="text-blue-400 text-2xl mb-2">🛡️</div>
              <div className="text-white font-medium">Insurance Protection</div>
            </div>
          </div>
        </div>
      </div>

      {/* Contact CTA */}
      <div className="max-w-3xl mx-auto px-6 py-16 text-center">
        <div className="bg-gray-800/30 rounded-2xl p-12 border border-gray-700/50">
          <div className="text-blue-400 text-4xl mb-6">✉️</div>
          <Heading 
            text="Need Legal Assistance?" 
            className="text-3xl font-bold mb-4 text-white"
          />
          <p className="text-gray-400 mb-8">
            Our legal team is available to answer questions about our policies, compliance framework, or your rights as a user.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              text="Contact Legal Team" 
              onClick={() => window.location.href = 'mailto:legal@laani.io'} 
              className="bg-blue-600 hover:bg-blue-700" 
            />
            <Button 
              text="FAQ Center →" 
              onClick={() => window.location.href = '/faq'} 
              className="bg-transparent border border-gray-600 hover:border-blue-400" 
            />
          </div>
        </div>
      </div>
    </Section>
  );
};

export default Legal;




